import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { TextInput, launchToast, ToastTypes } from '../../components'
import { Button, WemoloTypography } from '../../components/Wemolo'
import { Textarea } from 'baseui/textarea'
import { setTabTitle } from '../../utils'
import styles from './OccupancyApiTest.module.css'

const AUTH_URL = 'https://park-depot-authentifizierung.eu.auth0.com/oauth/token'
const OCCUPANCY_URL = 'https://api.prod.park-depot.de/occupancy/'
const CLIENT_ID = 'O7spflFvzzhhVvS6W2byotMkfnneGroJ'
const AUDIENCE = 'https://api.prod.park-depot.de'
const REDIRECT_URI = 'https://app.park-depot.de'

export const OccupancyApiTestContainer = () => {
    const { testOccupancyApi } = useFlags()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [manualToken, setManualToken] = useState('')
    const [curlCopied, setCurlCopied] = useState(false)
    const [occupancyCurlCopied, setOccupancyCurlCopied] = useState(false)

    useEffect(() => {
        setTabTitle('Occupancy Api Test')
    }, [])

    const generateTokenCurlCommand = () => {
        const payload = {
            client_id: CLIENT_ID,
            audience: AUDIENCE,
            grant_type: 'password',
            redirect_uri: REDIRECT_URI,
            scope: 'openid',
            username: email || '<YOUR_EMAIL>',
            password: password || '<YOUR_PASSWORD>'
        }
        return `curl -H "Content-type: application/json" -d '${JSON.stringify(payload)}' -X POST "${AUTH_URL}"`
    }

    const generateOccupancyCurlCommand = () => {
        const token = manualToken.trim() || '<YOUR_ACCESS_TOKEN>'
        return `curl -H "Authorization: Bearer ${token}" -X GET "${OCCUPANCY_URL}"`
    }

    const copyTokenCurlCommand = () => {
        navigator.clipboard.writeText(generateTokenCurlCommand())
        setCurlCopied(true)
        launchToast(ToastTypes.Success, 'Token curl command copied to clipboard!')
        setTimeout(() => setCurlCopied(false), 2000)
    }

    const copyOccupancyCurlCommand = () => {
        navigator.clipboard.writeText(generateOccupancyCurlCommand())
        setOccupancyCurlCopied(true)
        launchToast(ToastTypes.Success, 'Occupancy curl command copied to clipboard!')
        setTimeout(() => setOccupancyCurlCopied(false), 2000)
    }

    const parseTokenFromResponse = () => {
        try {
            const parsed = JSON.parse(manualToken)
            if (parsed.access_token) {
                setManualToken(parsed.access_token)
                launchToast(ToastTypes.Success, 'Access token extracted from JSON response!')
            }
        } catch {
            // Not JSON, assume it's the raw token - that's fine
        }
    }

    if (!testOccupancyApi) return null

    return (
        <div className={styles.root}>
            <WemoloTypography variant="title-xsmall" tag="span">
                Occupancy Api Test
            </WemoloTypography>

            <div className={styles.infoBanner}>
                <WemoloTypography variant="body-medium" tag="span">
                    Due to CORS restrictions, both API calls must be made via terminal/curl. This tool helps you
                    generate the commands.
                </WemoloTypography>
            </div>

            {/* Step 1: Generate Token Curl Command */}
            <div className={styles.step}>
                <WemoloTypography variant="body-medium-emphasized" tag="span">
                    Step 1: Get Authentication Token
                </WemoloTypography>

                <div className={styles.field}>
                    <WemoloTypography variant="body-medium" tag="span">
                        Email
                    </WemoloTypography>
                    <TextInput
                        value={email}
                        onChange={(e) => setEmail(e.currentTarget.value)}
                        placeholder="Enter email (e.g. parking_lot@customer.de)"
                        overrides={{
                            Root: {
                                style: {
                                    backgroundColor: 'white'
                                }
                            }
                        }}
                    />
                </div>

                <div className={styles.field}>
                    <WemoloTypography variant="body-medium" tag="span">
                        Password
                    </WemoloTypography>
                    <TextInput
                        value={password}
                        onChange={(e) => setPassword(e.currentTarget.value)}
                        placeholder="Enter password"
                        type="password"
                        overrides={{
                            Root: {
                                style: {
                                    backgroundColor: 'white'
                                }
                            }
                        }}
                    />
                </div>

                <div className={styles.field}>
                    <WemoloTypography variant="body-medium" tag="span">
                        Curl Command (run in terminal)
                    </WemoloTypography>
                    <WemoloTypography variant="label-xsmall" tag="div" className={styles.codeBlock}>
                        {generateTokenCurlCommand()}
                    </WemoloTypography>
                    <Button onClick={copyTokenCurlCommand} view="secondary">
                        {curlCopied ? 'Copied!' : 'Copy Curl Command'}
                    </Button>
                </div>

                <div className={styles.fieldOffset}>
                    <WemoloTypography variant="body-medium" tag="span">
                        Paste Access Token (from curl response)
                    </WemoloTypography>
                    <Textarea
                        value={manualToken}
                        onChange={(e) => setManualToken(e.currentTarget.value)}
                        onBlur={parseTokenFromResponse}
                        placeholder="Paste the access_token value here, or paste the entire JSON response"
                        overrides={{
                            Root: {
                                style: {
                                    backgroundColor: 'white',
                                    minHeight: '80px'
                                }
                            }
                        }}
                    />
                    <WemoloTypography variant="body-medium" tag="span">
                        Tip: You can paste the entire JSON response - the access_token will be extracted automatically.
                    </WemoloTypography>
                </div>
            </div>

            {/* Step 2: Get Occupancy Data */}
            <div className={classNames(styles.step, { [styles.stepDisabled]: !manualToken })}>
                <WemoloTypography variant="body-medium-emphasized" tag="span">
                    Step 2: Fetch Occupancy Data
                </WemoloTypography>

                <div className={styles.field}>
                    <WemoloTypography variant="body-medium" tag="span">
                        Curl Command (run in terminal)
                    </WemoloTypography>
                    <WemoloTypography variant="label-xsmall" tag="div" className={styles.codeBlock}>
                        {generateOccupancyCurlCommand()}
                    </WemoloTypography>
                    <Button onClick={copyOccupancyCurlCommand} view="secondary" disabled={!manualToken.trim()}>
                        {occupancyCurlCopied ? 'Copied!' : 'Copy Curl Command'}
                    </Button>
                </div>
            </div>
        </div>
    )
}
